import { api, unwrap } from '../../shared/api/client';
import type { components } from '../../shared/api/schema.gen';

export type TSource = components['schemas']['SourceResDto'];
export type TSourceUpdate = components['schemas']['UpdateSourceReqDto'];
export type TSourceSecret = components['schemas']['SourceSecretResDto'];
export type TSourceAuthMethod = components['schemas']['SourceAuthMethodResDto'];
export type TSourceAuthMethodCreate = components['schemas']['CreateSourceAuthMethodReqDto'];
export type TSourceAuthMethodUpdate = components['schemas']['UpdateSourceAuthMethodReqDto'];
export type TCreatedSourceSecret = components['schemas']['CreatedSourceSecretResDto'];
export type TRotatedSourceSecret = components['schemas']['RotateSourceSecretResDto'];
export type TTestSend = components['schemas']['SourceTestSendResDto'];
export type TTestSendBody = components['schemas']['TestWebhookSendReqDto'];
export type TGraceHours = components['schemas']['RotateSourceSecretReqDto']['graceHours'];

export type TSourceCreate = components['schemas']['CreateSourceReqDto'];

export const listSources = () =>
  unwrap(api.GET('/v1/sources', { params: { query: { limit: 100, withTotal: true } } }));

// A picker's search: the server filters by name, so a workspace past the first page is found.
export const searchSources = async (q: string) =>
  (await unwrap(api.GET('/v1/sources', { params: { query: { q, limit: 50 } } }))).data.map(
    (item) => ({ value: item.id, label: item.name }),
  );

export const createSource = (body: TSourceCreate): Promise<TSource> =>
  unwrap(api.POST('/v1/sources', { body }));

export const getSource = (id: string): Promise<TSource> =>
  unwrap(api.GET('/v1/sources/{id}', { params: { path: { id } } }));

export const updateSource = (id: string, body: TSourceUpdate): Promise<TSource> =>
  unwrap(api.PATCH('/v1/sources/{id}', { params: { path: { id } }, body }));

export const sourceSecrets = (id: string): Promise<TSourceSecret[]> =>
  unwrap(api.GET('/v1/sources/{id}/secrets', { params: { path: { id } } }));

export const sourceAuthMethods = (id: string): Promise<TSourceAuthMethod[]> =>
  unwrap(api.GET('/v1/sources/{id}/auth-methods', { params: { path: { id } } }));

export const createSourceAuthMethod = (
  id: string,
  body: TSourceAuthMethodCreate,
): Promise<TSourceAuthMethod> =>
  unwrap(api.POST('/v1/sources/{id}/auth-methods', { params: { path: { id } }, body }));

export const updateSourceAuthMethod = (
  id: string,
  methodId: string,
  body: TSourceAuthMethodUpdate,
): Promise<TSourceAuthMethod> =>
  unwrap(
    api.PATCH('/v1/sources/{id}/auth-methods/{methodId}', {
      params: { path: { id, methodId } },
      body,
    }),
  );

// Cascades: every secret stored against the method goes with it, unrecoverably.
export const deleteSourceAuthMethod = async (id: string, methodId: string): Promise<void> => {
  await unwrap(
    api.DELETE('/v1/sources/{id}/auth-methods/{methodId}', {
      params: { path: { id, methodId } },
    }),
  );
};

export const addSourceSecret = (
  id: string,
  methodId: string,
  value?: string,
): Promise<TCreatedSourceSecret> =>
  unwrap(
    api.POST('/v1/sources/{id}/auth-methods/{methodId}/secrets', {
      params: { path: { id, methodId } },
      body: value ? { value } : {},
    }),
  );

export const rotateSourceSecret = (
  id: string,
  methodId: string,
  body: { graceHours: TGraceHours; value?: string },
): Promise<TRotatedSourceSecret> =>
  unwrap(
    api.POST('/v1/sources/{id}/auth-methods/{methodId}/rotate-secret', {
      params: { path: { id, methodId } },
      body,
    }),
  );

export const revokeSourceSecret = (id: string, secretId: string): Promise<TSourceSecret> =>
  unwrap(
    api.POST('/v1/sources/{id}/secrets/{secretId}/revoke', {
      params: { path: { id, secretId } },
    }),
  );

export const testSend = (id: string, body: TTestSendBody): Promise<TTestSend> =>
  unwrap(api.POST('/v1/sources/{id}/test-send', { params: { path: { id } }, body }));

// The list screen's page: a search runs on the server, so it reaches past the first hundred.
export const listSourcesPage = (query: { q?: string; cursor?: string }) =>
  unwrap(
    api.GET('/v1/sources', {
      params: { query: { limit: 100, withTotal: true, q: query.q, cursor: query.cursor } },
    }),
  );
